import { Skeleton } from '@zond/ui';
import { BarChart2, Monitor } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { ChartTooltip, PieArc, PieArcSeries, PieChart } from 'reaviz';

interface DataItem {
  key: string;
  data: number;
  color?: string;
  originalData?: Array<{ key: string; data: number; color?: string }>;
}

interface PieChartToolProps {
  isLoading?: boolean;
  data: Array<{
    key: string;
    data: number;
    color?: string;
  }>;
  width?: number;
  height?: number;
  colors?: string[];
  threshold?: number; // Threshold percentage for grouping into "Other"
  maxItems?: number; // Maximum number of individual items (excluding "Other")
}

// Update interface to match actual data
interface TooltipData {
  y: number;
  x: string;
}

interface PieArcDatum {
  y: number;
  x: string;
}

interface AggregatedDataItem {
  key: string;
  data: number;
  totalTime: number;
  sessions: number;
  color: string;
  avgSessionTime: number;
  percentageOfTotal: number;
  originalData?: AggregatedDataItem[];
}

interface ProcessedDataItem {
  key: string;
  data: number;
  color: string;
  originalData?: AggregatedDataItem[];
}

const aggregateData = (data: DataItem[]): AggregatedDataItem[] => {
  // Group data by key
  const aggregated = data.reduce<Record<string, AggregatedDataItem>>((acc, item) => {
    if (!acc[item.key]) {
      acc[item.key] = {
        key: item.key,
        data: 0,
        totalTime: 0,
        sessions: 0,
        color: item.color || '#cccccc',
        avgSessionTime: 0,
        percentageOfTotal: 0,
      };
    }
    acc[item.key].totalTime += item.data;
    acc[item.key].data = acc[item.key].totalTime;
    acc[item.key].sessions++;
    return acc;
  }, {});

  const totalTime = Object.values(aggregated).reduce((sum, item) => sum + item.totalTime, 0);

  // Transform to array and add additional metrics
  return Object.values(aggregated).map((item) => ({
    ...item,
    avgSessionTime: item.totalTime / item.sessions,
    percentageOfTotal: (item.totalTime / totalTime) * 100,
  }));
};

export const PieChartTool = ({
  isLoading = false,
  data,
  width = 300,
  height = 300,
  colors,
  threshold = 5,
  maxItems = 10,
}: PieChartToolProps) => {
  // Change type to Map to store visibility state considering source (main/other)
  const [hiddenSectors, setHiddenSectors] = useState<Map<string, Set<'main' | 'other'>>>(new Map());

  const { processedData, mainItems, otherItems, totalTime } = useMemo(() => {
    if (!data || data.length === 0)
      return {
        processedData: [],
        mainItems: [],
        otherItems: [],
        totalTime: 0,
      };

    // Aggregate data
    const aggregatedItems = aggregateData(data);

    // Sort by total time
    const sortedItems = aggregatedItems.sort((a, b) => b.totalTime - a.totalTime);

    // Take top-10 for mainItems
    const main = sortedItems.slice(0, maxItems);

    // The rest goes to other
    const other = sortedItems.slice(maxItems);

    const total = sortedItems.reduce((sum, item) => sum + item.totalTime, 0);

    // Form processedData
    const processed: ProcessedDataItem[] = main.map((item) => ({
      key: item.key,
      data: item.totalTime,
      color: item.color,
    }));

    if (other.length > 0) {
      const otherTotal = other.reduce((sum, item) => sum + item.totalTime, 0);
      processed.push({
        key: 'Other',
        data: otherTotal,
        color: '#808080',
        originalData: other,
      });
    }

    return {
      processedData: processed.filter((item) => {
        if (item.key === 'Other') {
          const hiddenState = hiddenSectors.get('Other');
          return !hiddenState?.has('main');
        }
        const hiddenState = hiddenSectors.get(item.key);
        return !hiddenState?.has('main');
      }),
      mainItems: main,
      otherItems: other,
      totalTime: total,
    };
  }, [data, colors, threshold, maxItems, hiddenSectors]);

  const toggleSector = (key: string, source: 'main' | 'other') => {
    console.log('Toggling sector:', { key, source });
    setHiddenSectors((prev) => {
      const newMap = new Map(prev);
      const currentState = newMap.get(key) || new Set();
      const newState = new Set(currentState);

      if (newState.has(source)) {
        newState.delete(source);
      } else {
        newState.add(source);
      }

      if (newState.size === 0) {
        newMap.delete(key);
      } else {
        newMap.set(key, newState);
      }

      return newMap;
    });
  };

  // Function to check visibility state
  const isHidden = (key: string, source: 'main' | 'other') => {
    const hiddenState = hiddenSectors.get(key);
    return hiddenState?.has(source) || false;
  };

  if (isLoading) {
    return <Skeleton className="w-full h-[300px]" />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center">
        <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
          <Monitor className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Application Data</h3>
        <p className="text-muted-foreground">
          Application usage data will appear here
          <br />
          once it is collected.
        </p>
      </div>
    );
  }

  const colorScheme: string[] = processedData.map((item) => item.color || '#cccccc');

  return (
    <div className="flex flex-col h-full justify-between">
      <div className="flex-none flex items-center justify-center">
        <PieChart
          height={280}
          width={400}
          data={processedData}
          series={
            <PieArcSeries
              colorScheme={colorScheme}
              doughnut={true}
              innerRadius={0.6}
              arc={
                <PieArc
                  tooltip={
                    <ChartTooltip
                      followCursor={true}
                      content={({ y, x }: PieArcDatum) => {
                        if (typeof y === 'undefined' || !x) {
                          return null;
                        }

                        const percent = (y / totalTime) * 100;

                        return (
                          <div
                            className="bg-card p-2 rounded shadow-md border"
                            style={{ zIndex: 9999, position: 'relative' }}
                          >
                            <div className="font-bold">{x}</div>
                            <div>{formatTime(y)}</div>
                            <div>{percent.toFixed(1)}%</div>
                          </div>
                        );
                      }}
                    />
                  }
                />
              }
            />
          }
        />
      </div>

      <div className="mt-auto">
        <div className="overflow-y-auto max-h-[200px] custom-scrollbar px-6 mb-6">
          <div className="space-y-1">
            {mainItems.map((item, index) => {
              const hidden = isHidden(item.key, 'main');
              return (
                <div
                  key={index}
                  className="flex items-center text-sm cursor-pointer hover:bg-muted/50 py-0.5 px-1 rounded transition-colors"
                  onClick={() => toggleSector(item.key, 'main')}
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0 transition-opacity"
                    style={{
                      backgroundColor: item.color || '#cccccc',
                      opacity: hidden ? 0.3 : 1,
                    }}
                  ></div>
                  <div className="flex-1 truncate" style={{ opacity: hidden ? 0.5 : 1 }}>
                    {item.key}
                  </div>
                  <div className="ml-2 text-right whitespace-nowrap" style={{ opacity: hidden ? 0.5 : 1 }}>
                    {formatTime(item.data)}
                    <span className="text-gray-500 ml-1">
                      ({((item.data / totalTime) * 100).toFixed(0)}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {otherItems.length > 0 && (
            <div className="mt-2 border-t pt-2">
              <details>
                <summary className="text-sm font-medium cursor-pointer">Other apps details</summary>
                <div className="mt-1 pl-2 space-y-0.5">
                  {otherItems.map((item, index) => {
                    const hidden = isHidden(item.key, 'other');
                    return (
                      <div
                        key={index}
                        className="flex items-center text-xs cursor-pointer hover:bg-muted/50 py-0.5 px-1 rounded transition-colors"
                        onClick={() => toggleSector(item.key, 'other')}
                      >
                        <div
                          className="w-2 h-2 rounded-full mr-1 flex-shrink-0 transition-opacity"
                          style={{
                            backgroundColor: item.color || '#808080',
                            opacity: hidden ? 0.3 : 1,
                          }}
                        ></div>
                        <div className="flex-1 truncate" style={{ opacity: hidden ? 0.5 : 1 }}>
                          {item.key}
                        </div>
                        <div className="ml-2 whitespace-nowrap" style={{ opacity: hidden ? 0.5 : 1 }}>
                          {formatTime(item.data)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return `${Math.floor(seconds)}s`;
  }
};
